import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaPlay } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import Field, { NativeSelect, TextInput } from '../../../components/ui/Field';
import Combo from '../../../components/ui/Combo';
import ExprInput from '../../Tools/editor/ExprInput';
import { allNodes, slugify } from '../../../utils/toolDefinition';
import DataView from '../../Tools/editor/DataView';
import { InsertProvider, useInsert } from '../../Tools/editor/InsertContext';
import { engineService } from '../../../services/engineService';
import { varsFromSample } from '../../../utils/templateAssist';
import { usePreviewSession } from './PromptExtras';

// DATOS PREVIOS del agente: herramientas que corren ANTES de que el mensaje llegue al modelo (por ejemplo «buscar el contacto por su
// teléfono»). Su resultado se usa en las Instrucciones como {{datos.<nombre>.texto}} o un campo de un paso:
// {{datos.<nombre>.nodos.<paso>.data.0.<columna>}}. Como aquí todavía no hay IA, los datos que la herramienta espera de la IA se llenan
// con plantillas: {{contacto.telefono}}, {{mensaje}}… Se guarda en el borrador (config.pre_tools) y se usa al publicar.
export const PROMPT_VARS = [
  { path: 'contacto.nombre', label: 'Nombre en el canal', group: 'Conversación' },
  { path: 'contacto.telefono', label: 'Teléfono (WhatsApp)', group: 'Conversación' },
  { path: 'contacto.canal', label: 'Canal (whatsapp, telegram, web)', group: 'Conversación' },
  { path: 'mensaje', label: 'Lo que escribió la persona', group: 'Conversación' },
  { path: 'ahora', label: 'Fecha y hora (ISO)', group: 'Conversación' },
];

// Pasos que cambian datos: como dato previo correrían en CADA mensaje (insertar filas, mandar POST…). Se avisa, no se bloquea.
export const writesData = (def) => allNodes(def?.nodes || []).some((n) =>
  (n.type === 'supabase' && ['insert', 'upsert', 'update', 'rpc'].includes(n.operation)) || (n.type === 'sheets' && ['append', 'update'].includes(n.operation))
  || (n.type === 'http' && n.method && n.method !== 'GET'));

export default function PreToolsEditor(props) {
  return <InsertProvider><PreTools {...props} /></InsertProvider>;
}

function PreTools({ agent, draft, tools, defs, connectors, readOnly, onSave }) {
  const saved = useMemo(() => (Array.isArray(draft?.config?.pre_tools) ? draft.config.pre_tools : []), [draft]);
  const [list, setList] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { sample, setSample, run, setRun } = usePreviewSession(agent.id);
  const [testing, setTesting] = useState({ status: 'idle' });
  useEffect(() => { setList(saved); }, [saved]);
  const dirty = JSON.stringify(list) !== JSON.stringify(saved);
  const byId = Object.fromEntries(tools.map((t) => [t.id, t]));
  const available = tools.filter((t) => t.published_version && t.status !== 'archived');
  const usable = (kind) => connectors.filter((c) => c.kind === kind && (c.project_id === null || c.project_id === agent.project_id));
  const up = (i, patch) => setList((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const add = (toolId) => {
    const t = byId[toolId]; if (!t) return;
    const base = (slugify(t.slug.replace(/^(buscar|consultar|leer|obtener)_/, '')) || 'datos').slice(0, 36);
    const taken = new Set(list.map((p) => p.as));
    let as = base; for (let n = 2; taken.has(as); n++) as = `${base}_${n}`;
    const def = defs[t.id] || {};
    const inputs = Object.fromEntries((def.inputs || []).filter((x) => x.source === 'ai').map((x) => [x.key, /tel|phone|cel|whats/i.test(x.key) ? '{{contacto.telefono}}' : /nombre|name/i.test(x.key) ? '{{contacto.nombre}}' : '']));
    setList((l) => [...l, { tool_id: t.id, as, inputs, connectors: {} }]);
  };
  const AS = /^[a-z][a-z0-9_]{0,39}$/;
  const problem = list.map((p) => (!AS.test(p.as) ? `«${p.as || '(vacío)'}»: el nombre empieza con letra y usa solo minúsculas, números y _.` : list.filter((q) => q.as === p.as).length > 1 ? `«${p.as}» está repetido: cada uno necesita su propio nombre.` : '')).find(Boolean);
  const save = async () => {
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    try { await onSave(list); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  // Prueba con lo que hay en pantalla (aunque no esté guardado): corre las herramientas DE VERDAD con el contacto de prueba.
  const test = async () => {
    if (problem) { setError(problem); return; }
    setTesting({ status: 'loading' });
    try {
      const r = await engineService.promptPreview(agent.id, { prompt: '', config: { ...(draft?.config || {}), pre_tools: list }, contacto: { telefono: sample.telefono || null, nombre: sample.nombre, canal: sample.canal }, mensaje: sample.mensaje });
      setRun({ ...r, for: JSON.stringify(list) });
      setTesting({ status: 'ready' });
    } catch (e) { setTesting({ status: 'error', error: e.message }); }
  };
  const stale = run && run.for !== JSON.stringify(list);
  const setS = (k) => (e) => setSample({ ...sample, [k]: e.target.value });

  return (
    <section aria-labelledby="pre-title">
      <h3 id="pre-title" className="sr-only">Antes de responder</h3>
      <p className="mb-4 text-[13.5px] text-fg-dim max-w-[72ch]">
        Herramientas que corren <b className="text-fg">antes</b> de que el mensaje llegue al agente, para darle datos de entrada: por ejemplo buscar a la persona por su teléfono o leer una lista de palabras.
        Su resultado se pone en las <Link to={`/agentes/${agent.id}/instrucciones`} className="text-live hover:underline">Instrucciones</Link> donde escribas <code className="font-mono">{'{{datos.<nombre>.texto}}'}</code>. Si una falla, el agente responde igual, sin esos datos.
      </p>

      {list.length > 0 && (
        <div className="mb-4 rounded-2xl bg-raised/50 ring-1 ring-line p-4">
          <p className="text-[13px] font-semibold text-fg">Probar con un contacto</p>
          <p className="mt-0.5 mb-3 text-[12.5px] text-fg-dim">Corre las herramientas de verdad (consultan tu base o tu API) y te muestra, en cada una, qué recibió, qué devolvió y qué escribir en las Instrucciones para usarlo.</p>
          <div className="grid gap-3 sm:grid-cols-[repeat(4,minmax(0,1fr))_auto] sm:items-end">
            <Field label="Teléfono"><TextInput value={sample.telefono} onChange={setS('telefono')} inputMode="tel" /></Field>
            <Field label="Nombre"><TextInput value={sample.nombre} onChange={setS('nombre')} /></Field>
            <Field label="Canal">
              <NativeSelect value={sample.canal} onChange={setS('canal')}><option value="whatsapp">WhatsApp</option><option value="telegram">Telegram</option><option value="web">Chat web</option></NativeSelect>
            </Field>
            <Field label="Mensaje"><TextInput value={sample.mensaje} onChange={setS('mensaje')} /></Field>
            <Btn variant="primary" icon={FaPlay} onClick={test} loading={testing.status === 'loading'}>Probar</Btn>
          </div>
          {testing.status === 'error' && <p role="alert" className="mt-2 text-[13px] text-signal">{testing.error}</p>}
          {stale && <p className="mt-2 text-[12.5px] text-caution">Cambiaste algo después de la última prueba: vuelve a probar para ver los datos al día.</p>}
          {run?.skipped?.length > 0 && <p className="mt-2 text-[12.5px] text-caution">No corrieron: {run.skipped.join(', ')}</p>}
        </div>
      )}

      <ul className="space-y-3">
        {list.map((p, i) => {
          const t = byId[p.tool_id]; const def = defs[p.tool_id] || {};
          const aiInputs = (def.inputs || []).filter((x) => x.source === 'ai');
          const slots = (def.connectors || []).filter((s) => s.mode === 'agent');
          const vars = [...PROMPT_VARS, ...list.slice(0, i).flatMap((q) => (run?.datos?.[q.as]
            ? varsFromSample({ datos: { [q.as]: run.datos[q.as] } }, 'Datos previos anteriores').filter((v) => v.path.split('.').length > 2)
            : [{ path: `datos.${q.as}.texto`, label: `Resultado de «${byId[q.tool_id]?.name || q.as}»`, group: 'Datos previos anteriores' }]))];
          const paso = run?.pasos?.find((x) => x.as === p.as);
          return (
            <li key={i} className="rounded-2xl bg-panel ring-1 ring-line p-4 sm:p-5 space-y-4">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14.5px] font-semibold text-fg">{t ? t.name : `Herramienta ${p.tool_id} (ya no existe)`} <span className="ml-1 text-[12px] font-normal text-fg-faint">{i + 1}.º en correr</span></p>
                  <p className="text-[12.5px] text-fg-dim">{def.description || t?.draft?.description}</p>
                  {writesData(def) && <p role="note" className="mt-1.5 text-[12.5px] text-caution">Esta herramienta guarda o cambia datos: aquí correría con cada mensaje que llegue. Úsala solo si eso es lo que quieres; para consultar, usa una que solo busque.</p>}
                </div>
                {t && <Link to={`/herramientas/${t.id}`} className="inline-flex items-center h-8 px-3.5 rounded-full text-[13px] font-semibold text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">Editar</Link>}
                <Btn size="sm" variant="ghost" icon={FaTimes} onClick={() => setList((l) => l.filter((_, j) => j !== i))} disabled={readOnly}>Quitar</Btn>
              </div>
              <Field label="Nombre de sus datos" hint={`En las Instrucciones se usan como {{datos.${p.as || '…'}…}}.`} className="sm:max-w-xs">
                <TextInput value={p.as} onChange={(e) => up(i, { as: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} className="font-mono" disabled={readOnly} />
              </Field>
              {aiInputs.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[12.5px] font-semibold text-fg-dim">Con qué datos la busca</p>
                  {aiInputs.map((x) => (
                    <Field key={x.key} label={x.key} hint={x.description}>
                      <ExprInput value={p.inputs?.[x.key] || ''} onChange={(v) => up(i, { inputs: { ...(p.inputs || {}), [x.key]: v } })} vars={vars} preview={false} ariaLabel={`Valor de ${x.key}`} placeholder="{{contacto.telefono}}" />
                    </Field>
                  ))}
                </div>
              )}
              {slots.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {slots.map((s) => (
                    <Field key={s.slot} label={s.label || s.slot} hint={usable(s.kind).length ? undefined : 'No hay conectores de este tipo para este cliente: agrégalo en Integraciones.'}>
                      <NativeSelect value={p.connectors?.[s.slot] || ''} disabled={readOnly} onChange={(e) => up(i, { connectors: { ...(p.connectors || {}), [s.slot]: Number(e.target.value) || undefined } })}>
                        <option value="">Elige el conector</option>
                        {usable(s.kind).map((c) => <option key={c.id} value={c.id}>{c.name}{c.project_id === null ? ' (global)' : ''}</option>)}
                      </NativeSelect>
                    </Field>
                  ))}
                </div>
              )}
              <Result as={p.as} paso={paso} datos={run?.datos?.[p.as]} tested={Boolean(run)} />
            </li>
          );
        })}
      </ul>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-80">
          <Combo value="" onChange={(v) => add(Number(v))} disabled={readOnly || list.length >= 5} ariaLabel="Agregar una herramienta antes de responder" placeholder={list.length >= 5 ? 'Máximo 5' : '+ Agregar una herramienta'}
            options={available.map((t) => ({ value: String(t.id), label: t.name, hint: `${writesData(defs[t.id]) ? 'Guarda datos · ' : ''}${t.draft?.description || t.slug}` }))} emptyText="No hay herramientas publicadas. Créalas en Herramientas." />
        </div>
        {dirty && <Btn variant="primary" onClick={save} loading={busy} disabled={readOnly}>Guardar en el borrador</Btn>}
        {dirty && <Btn variant="ghost" onClick={() => setList(saved)} disabled={busy}>Descartar</Btn>}
        {(error || problem) && <p role="alert" className="basis-full text-[13px] text-signal">{error || problem}</p>}
      </div>
    </section>
  );
}

/** Campos de la salida de una herramienta listos para las Instrucciones: columnas de la primera fila de cada paso (o sus llaves). */
function fieldsOf(as, datos, max = 24) {
  const out = [];
  for (const [id, o] of Object.entries(datos?.nodos || {})) {
    const d = o && typeof o === 'object' && 'data' in o ? o.data : o;
    const base = `datos.${as}.nodos.${id}${o && typeof o === 'object' && 'data' in o ? '.data' : ''}`;
    if (Array.isArray(d)) {
      out.push({ path: `${base} | cantidad`, value: d.length, label: `cuántas filas devolvió «${id}»` });
      const row = d[0];
      if (row && typeof row === 'object') for (const [k, v] of Object.entries(row)) if (/^[A-Za-z0-9_-]+$/.test(k)) out.push({ path: `${base}.0.${k}`, value: v, label: `${k} (primera fila de «${id}»)` });
    } else if (d && typeof d === 'object') {
      for (const [k, v] of Object.entries(d)) if (/^[A-Za-z0-9_-]+$/.test(k)) out.push({ path: `${base}.${k}`, value: v, label: `${k} de «${id}»` });
    }
    if (out.length >= max) break;
  }
  return out.slice(0, max);
}
const show = (v) => (v === null || v === undefined ? 'vacío' : typeof v === 'object' ? JSON.stringify(v).slice(0, 80) : String(v).slice(0, 80));

/** Lo que pasó al probar una herramienta: qué recibió, qué devolvió y qué escribir en las Instrucciones (clic = insertar o copiar). */
function Result({ as, paso, datos, tested }) {
  const { insert } = useInsert();
  const [flash, setFlash] = useState('');
  const pick = (path) => {
    const t = `{{${path}}}`;
    const done = (m) => { setFlash(m); setTimeout(() => setFlash(''), 1600); };
    if (insert(t)) done(`Insertado ${t}`);
    else navigator.clipboard?.writeText(t).then(() => done(`Copiado ${t} — pégalo en las Instrucciones`)).catch(() => done(t));
  };
  if (!tested) return <p className="rounded-xl border border-dashed border-fg/15 px-4 py-3 text-[12.5px] text-fg-dim">Usa «Probar» (arriba) para ver aquí qué devuelve esta herramienta y qué escribir en las Instrucciones para usarlo.</p>;
  if (!paso) return <p className="rounded-xl border border-dashed border-caution/40 px-4 py-3 text-[12.5px] text-caution">No corrió en la prueba (¿no está publicada o le falta el conector?).</p>;
  const fields = fieldsOf(as, datos);
  const plain = fields.filter((f) => !f.path.includes('|'));
  const first = plain.find((f) => !/\.(id|uuid|created_at|updated_at)$/.test(f.path) && typeof f.value === 'string') || plain[0];
  return (
    <div className="rounded-xl ring-1 ring-line overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-raised/50 border-b border-line text-[12.5px]">
        <span className={`inline-flex items-center gap-1.5 font-semibold ${paso.ok ? 'text-live' : 'text-signal'}`}><span aria-hidden className={`h-1.5 w-1.5 rounded-full ${paso.ok ? 'bg-live' : 'bg-signal'}`} />{paso.ok ? 'Funcionó' : 'Falló'}</span>
        <span className="text-fg-faint tabular-nums">{paso.ms} ms</span>
        <span role="status" aria-live="polite" className="ml-auto truncate font-mono text-[11.5px] text-live">{flash}</span>
      </div>
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-line">
        <div className="p-4 min-w-0">
          <p className="text-[12px] font-semibold text-fg-dim mb-2">Recibió</p>
          {paso.inputs && Object.keys(paso.inputs).length
            ? <dl className="space-y-1 text-[12.5px]">{Object.entries(paso.inputs).map(([k, v]) => <div key={k} className="flex gap-2 min-w-0"><dt className="font-mono text-fg-faint shrink-0">{k}</dt><dd className="font-mono text-fg truncate">{show(v)}</dd></div>)}</dl>
            : <p className="text-[12.5px] text-fg-faint">Sin datos de entrada.</p>}
        </div>
        <div className="p-4 min-w-0">
          <p className="text-[12px] font-semibold text-fg-dim mb-2">Devolvió (su respuesta)</p>
          {paso.error ? <p className="text-[12.5px] text-signal">{paso.error}</p>
            : <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-raised/60 px-3 py-2 text-[12.5px] text-fg font-sans">{paso.texto || '(vacía)'}</pre>}
          <button type="button" onClick={() => pick(`datos.${as}.texto`)} className="mt-2 inline-flex items-center rounded-md bg-live-soft px-1.5 py-0.5 font-mono text-[12px] font-semibold text-live ring-1 ring-inset ring-live/50 hover:bg-live/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live">{`{{datos.${as}.texto}}`}</button>
          <span className="ml-2 text-[12px] text-fg-faint">pone toda la respuesta</span>
        </div>
      </div>
      {fields.length > 0 && (
        <div className="border-t border-line p-4">
          <p className="text-[12px] font-semibold text-fg-dim">Campos para usar en las Instrucciones <span className="font-normal text-fg-faint">· clic para copiar</span></p>
          <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {fields.map((f) => (
              <li key={f.path} className="min-w-0">
                <button type="button" onClick={() => pick(f.path)} title={f.label} className="group flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
                  <code className="shrink min-w-0 truncate rounded-md bg-live-soft px-1.5 py-0.5 text-[11.5px] font-semibold text-live ring-1 ring-inset ring-live/50">{`{{${f.path}}}`}</code>
                  <span className="shrink-0 text-fg-faint">→</span>
                  <span className="min-w-0 truncate text-[12.5px] text-fg">{show(f.value)}</span>
                </button>
              </li>
            ))}
          </ul>
          {first && (
            <p className="mt-3 text-[12.5px] text-fg-dim">Ejemplo: si en las Instrucciones escribes <code className="font-mono text-live">{`Dato del cliente: {{${first.path}}}`}</code>, el agente lee <b className="text-fg">Dato del cliente: {show(first.value)}</b>.</p>
          )}
        </div>
      )}
      {datos && (
        <details className="border-t border-line">
          <summary className="cursor-pointer px-4 py-2 text-[12.5px] text-fg-dim hover:text-fg">Ver todos los datos (tabla o JSON)</summary>
          <div className="px-4 pb-4"><DataView value={datos} base={`datos.${as}`} /></div>
        </details>
      )}
    </div>
  );
}

